import { useState, useCallback, useEffect } from 'react';
import { hasPhpBackend } from '@/lib/phpApi';
import { useAuth } from './useAuth';

export interface Notification {
  id: string;
  type: 'warning' | 'info' | 'success' | 'error';
  title: string;
  message: string;
  action?: string;
  read: boolean;
  timestamp: number;
}

let _notifications: Notification[] = [];
let _listeners: Set<() => void> = new Set();

function notify() { _listeners.forEach(fn => fn()); }

export function addNotification(n: Omit<Notification, 'id' | 'timestamp' | 'read'>) {
  const notif: Notification = {
    ...n,
    id: 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    timestamp: Date.now(),
    read: false,
  };
  _notifications = [notif, ..._notifications];
  notify();
}

export function markRead(id: string) {
  _notifications = _notifications.map(n => n.id === id ? { ...n, read: true } : n);
  notify();
}

export function markAllRead() {
  _notifications = _notifications.map(n => ({ ...n, read: true }));
  notify();
}

export function useNotifications() {
  const [, setTick] = useState(0);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fn = () => setTick(t => t + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  useEffect(() => {
    // Auto-detect issues and add notifications
    const checks: Omit<Notification, 'id' | 'timestamp' | 'read'>[] = [];

    // Check backend
    if (!hasPhpBackend()) {
      checks.push({
        type: 'warning',
        title: 'Backend Connection Issue',
        message: 'Database backend is not reachable. Data will be saved locally only. Upload the API folder to your server to enable database sync.',
        action: 'Upload the api/ folder from the deployment pack to your web server root.',
      });
    }

    // Check login
    if (!isAuthenticated) {
      checks.push({
        type: 'warning',
        title: 'Not Logged In',
        message: 'You are not logged in. Database features like saving assessments and reports require authentication.',
        action: 'Log in with your username and password to enable full database access.',
      });
    }

    // Check local data that could be synced
    const localAssessments = JSON.parse(localStorage.getItem('rb_local_assessments') || '[]');
    if (localAssessments.length > 0 && isAuthenticated && hasPhpBackend()) {
      checks.push({
        type: 'info',
        title: 'Unsynced Local Data',
        message: `You have ${localAssessments.length} local assessment(s) that could be synced to the database.`,
        action: 'Go to Reports tab to view and manage your assessments.',
      });
    }

    // Add checks that don't already exist
    checks.forEach(c => {
      const exists = _notifications.some(n => n.title === c.title && !n.read);
      if (!exists) addNotification(c);
    });
  }, [isAuthenticated]);

  const unreadCount = _notifications.filter(n => !n.read).length;

  return {
    notifications: _notifications,
    unreadCount,
    markRead,
    markAllRead,
    addNotification,
  };
}
